import {
  type Dispatch,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type SetStateAction,
  useCallback,
} from 'react'
import { isMiddleButtonPanPointerStart } from '@/lib/zoom/editor-canvas-zoom-pointer'
import type { DragCreateState } from '../types/drag-create-state'
import type {
  MarqueeState,
  MarqueeZoomDragState,
  TextDragCreateState,
} from '../types/canvas-drag-state'

type PointerHandler = (e: ReactPointerEvent<HTMLDivElement>) => void
/** A gesture's handler: true when the event was its gesture's, which ends the routing. */
type GesturePointerHandler = (e: ReactPointerEvent<HTMLDivElement>) => boolean

/**
 * The pasteboard's pointer router. Each gesture hook owns its own branch; this hands every
 * pointer event to them in a fixed order and stops at the first that claims it — text linking,
 * pan, the edit modes' click-away, the isolation exit, then the select / Type / shape tools'
 * gestures. Also clears every gesture on pointer cancel, and routes the scroller gutter's own
 * pointer-down.
 */
export function usePasteboardPointer({
  pasteboardRef,
  bleedWidth,
  bleedHeight,
  activeIsolationGroupId,
  exitGroupIsolationMode,
  getCanvasPointFromPointerEvent,
  handleTextLinkPointerDown,
  handleTextLinkPointerMove,
  handleTextToolHoverPointerMove,
  handleTextLinkPointerUp,
  handleMiddlePanPointerDown,
  handlePanPointerDown,
  handlePanDragPointerMove,
  handlePanDragPointerUp,
  endPanDrag,
  handleEditModeClickAwayPointerDown,
  handleMarqueeZoomPointerMove,
  handleMarqueeZoomPointerUp,
  setMarqueeZoom,
  handleMarqueeSelectPointerDown,
  handleMarqueeSelectPointerMove,
  handleMarqueeSelectPointerUp,
  setMarquee,
  handleTextDragCreatePointerDown,
  handleTextDragCreatePointerMove,
  handleTextDragCreatePointerUp,
  textDragCreateRef,
  textDragRafRef,
  setTextDragCreate,
  handleShapeDragCreatePointerDown,
  handleShapeDragCreatePointerMove,
  handleShapeDragCreatePointerUp,
  dragCreateRef,
  dragRafRef,
  setDragCreate,
}: {
  pasteboardRef: MutableRefObject<HTMLDivElement | null>
  bleedWidth: number
  bleedHeight: number
  activeIsolationGroupId: string | null
  exitGroupIsolationMode: () => void
  getCanvasPointFromPointerEvent: (
    clientX: number,
    clientY: number
  ) => { canvasX: number; canvasY: number } | null
  handleTextLinkPointerDown: GesturePointerHandler
  handleTextLinkPointerMove: PointerHandler
  handleTextToolHoverPointerMove: PointerHandler
  handleTextLinkPointerUp: GesturePointerHandler
  handleMiddlePanPointerDown: (
    e: ReactPointerEvent<HTMLDivElement>,
    captureElement?: HTMLDivElement | null
  ) => boolean
  handlePanPointerDown: GesturePointerHandler
  handlePanDragPointerMove: GesturePointerHandler
  handlePanDragPointerUp: GesturePointerHandler
  endPanDrag: () => void
  handleEditModeClickAwayPointerDown: GesturePointerHandler
  handleMarqueeZoomPointerMove: GesturePointerHandler
  handleMarqueeZoomPointerUp: GesturePointerHandler
  setMarqueeZoom: Dispatch<SetStateAction<MarqueeZoomDragState | null>>
  handleMarqueeSelectPointerDown: GesturePointerHandler
  handleMarqueeSelectPointerMove: GesturePointerHandler
  handleMarqueeSelectPointerUp: GesturePointerHandler
  setMarquee: Dispatch<SetStateAction<MarqueeState | null>>
  handleTextDragCreatePointerDown: GesturePointerHandler
  handleTextDragCreatePointerMove: GesturePointerHandler
  handleTextDragCreatePointerUp: GesturePointerHandler
  textDragCreateRef: MutableRefObject<TextDragCreateState | null>
  textDragRafRef: MutableRefObject<number | null>
  setTextDragCreate: Dispatch<SetStateAction<TextDragCreateState | null>>
  handleShapeDragCreatePointerDown: PointerHandler
  handleShapeDragCreatePointerMove: PointerHandler
  handleShapeDragCreatePointerUp: PointerHandler
  dragCreateRef: MutableRefObject<DragCreateState | null>
  dragRafRef: MutableRefObject<number | null>
  setDragCreate: Dispatch<SetStateAction<DragCreateState | null>>
}) {
  /** Group isolation: a click outside the document's own bounds exits isolation. */
  const handleIsolationExitPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      if (e.button === 0 && activeIsolationGroupId) {
        const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

        if (pt) {
          const clickedOutsideCanvasBounds =
            pt.canvasX < 0 || pt.canvasY < 0 || pt.canvasX > bleedWidth || pt.canvasY > bleedHeight

          if (clickedOutsideCanvasBounds) {
            e.preventDefault()
            exitGroupIsolationMode()
            return true
          }
        }
      }

      return false
    },
    [
      activeIsolationGroupId,
      bleedHeight,
      bleedWidth,
      exitGroupIsolationMode,
      getCanvasPointFromPointerEvent,
    ]
  )

  const handlePasteboardPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // Blur any focused panel input (e.g. an Object Effects field) before this gesture can
      // deselect the current shape on pointer-up. Deselection only clears `activeObjects`
      // later, on release — without this, the panel's pending edit would still be sitting in
      // local input state when the shape disappears from its `eligible` list, so the
      // subsequent blur-driven commit silently no-ops instead of applying to the shape.
      if (e.button === 0) {
        const focused = document.activeElement as HTMLElement | null

        if (focused && focused !== document.body) {
          focused.blur()
        }
      }

      if (handleTextLinkPointerDown(e)) return

      if (isMiddleButtonPanPointerStart(e) && handleMiddlePanPointerDown(e)) {
        return
      }

      if (handlePanPointerDown(e)) {
        return
      }

      if (handleEditModeClickAwayPointerDown(e)) return
      if (handleIsolationExitPointerDown(e)) return
      if (handleMarqueeSelectPointerDown(e)) return
      if (handleTextDragCreatePointerDown(e)) return
      handleShapeDragCreatePointerDown(e)
    },
    [
      handleTextLinkPointerDown,
      handleMiddlePanPointerDown,
      handlePanPointerDown,
      handleEditModeClickAwayPointerDown,
      handleIsolationExitPointerDown,
      handleMarqueeSelectPointerDown,
      handleTextDragCreatePointerDown,
      handleShapeDragCreatePointerDown,
    ]
  )

  const handlePasteboardPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      handleTextLinkPointerMove(e)
      handleTextToolHoverPointerMove(e)

      if (handlePanDragPointerMove(e)) return
      if (handleMarqueeZoomPointerMove(e)) return
      if (handleMarqueeSelectPointerMove(e)) return
      if (handleTextDragCreatePointerMove(e)) return
      handleShapeDragCreatePointerMove(e)
    },
    [
      handleTextLinkPointerMove,
      handleTextToolHoverPointerMove,
      handlePanDragPointerMove,
      handleMarqueeZoomPointerMove,
      handleMarqueeSelectPointerMove,
      handleTextDragCreatePointerMove,
      handleShapeDragCreatePointerMove,
    ]
  )

  const handlePasteboardPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (handleTextLinkPointerUp(e)) return
      if (handlePanDragPointerUp(e)) return
      if (handleMarqueeZoomPointerUp(e)) return
      if (handleMarqueeSelectPointerUp(e)) return
      if (handleTextDragCreatePointerUp(e)) return
      handleShapeDragCreatePointerUp(e)
    },
    [
      handleTextLinkPointerUp,
      handlePanDragPointerUp,
      handleMarqueeZoomPointerUp,
      handleMarqueeSelectPointerUp,
      handleTextDragCreatePointerUp,
      handleShapeDragCreatePointerUp,
    ]
  )

  const handlePasteboardPointerCancel = useCallback(() => {
    endPanDrag()
    dragCreateRef.current = null
    textDragCreateRef.current = null
    if (dragRafRef.current != null) {
      cancelAnimationFrame(dragRafRef.current)
      dragRafRef.current = null
    }

    if (textDragRafRef.current != null) {
      cancelAnimationFrame(textDragRafRef.current)
      textDragRafRef.current = null
    }

    setDragCreate(null)
    setTextDragCreate(null)
    setMarquee(null)
    setMarqueeZoom(null)
  }, [
    endPanDrag,
    dragCreateRef,
    textDragCreateRef,
    dragRafRef,
    textDragRafRef,
    setDragCreate,
    setTextDragCreate,
    setMarquee,
    setMarqueeZoom,
  ])

  /** The scroller gutter (outside the pasteboard): isolation exit and middle-button pan. */
  const handleScrollerPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button === 0 && e.target === e.currentTarget && !!activeIsolationGroupId) {
        e.preventDefault()
        exitGroupIsolationMode()
        return
      }

      if (!isMiddleButtonPanPointerStart(e)) return
      // Middle-click on the scroller gutter (not on the pasteboard subtree)
      if (e.target !== e.currentTarget) return
      const pb = pasteboardRef.current

      if (!pb) return
      void handleMiddlePanPointerDown(e, pb)
    },
    [activeIsolationGroupId, exitGroupIsolationMode, handleMiddlePanPointerDown, pasteboardRef]
  )

  return {
    handlePasteboardPointerDown,
    handlePasteboardPointerMove,
    handlePasteboardPointerUp,
    handlePasteboardPointerCancel,
    handleScrollerPointerDown,
  }
}
